import { COLLISION_CELLS_PER_TILE, drawnFrameX, drawnFrameY, nearestCellEdge, type ManifestPixelOffset } from "daedalus-core";
import { AUTHORING_CELL, FOOT_HALF_WIDTH, FOOT_HEIGHT, SIXTEENTHS } from "../movement/step.ts";

/**
 * Activation cells (ADR-053 decision 8 as amended): a thing is in range when any cell its user's feet box
 * covers lies in the cell set its current state carries. The set comes from the world manifest already
 * resolved and mirrored, so the map server and the client ask the same question of the same data and a sign
 * readable from the south is a matter of which cells were painted, not of a facing rule. Testing the whole
 * box gives a character standing against a footprint a whole cell of positions from which a verb reaches,
 * wider than a walking step, so a walker stops in range whatever sixteenth their steps land on.
 */

/**
 * A thing's activation cells: where it stands in sixteenths, the pixel width of the frame its current state
 * shows, whether the thing is mirrored, that state's `[column, rowsUp]` cells in the frame's own space,
 * already mirrored across the frame's columns when it is, and its appearance's pixel offset (undefined for an
 * appearance written without one), which puts the frame back on the author's pixel.
 */
export interface ActivationCells {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly flipX: boolean;
  readonly cells: readonly (readonly [number, number])[];
  readonly offset?: ManifestPixelOffset | undefined;
}

/**
 * Whether any cell of a character's feet box lies in a thing's activation cells. The box is the one
 * movement collides with (`FOOT_HALF_WIDTH`, `FOOT_HEIGHT`). The frame's bottom-left corner is where the frame
 * is drawn (`drawnFrameX`, `drawnFrameY`: the feet point moved by the appearance's pixel offset, ADR-040
 * amendment of 2026-10-04, P1.6-T83), as the collision stamp takes it, then laid on the collision cells as the
 * stamp lays the footprint (`frameCellSpace` in Daedalus, ADR-040 amendment of 2026-10-03 for P1.6-T78): its
 * left edge, or its right edge when mirrored, and its bottom on the nearest cell edges, so every cell of the
 * set is one world cell, lined up with the cells the thing blocks. Nothing is allocated.
 *
 * @param self The character's feet position in sixteenths.
 * @param activation The thing's activation cells.
 * @param tileSize Tile size in pixels.
 * @returns True when a verb can reach.
 */
export function inActivation(self: { readonly x: number; readonly y: number }, activation: ActivationCells, tileSize: number): boolean {
  if (activation.cells.length === 0) {
    return false;
  }
  const cellPixels = tileSize / COLLISION_CELLS_PER_TILE;
  const frameLeft = drawnFrameX(activation.x / SIXTEENTHS, activation.width, activation.offset?.x ?? 0, tileSize) * tileSize;
  const left = activation.flipX ? nearestCellEdge(frameLeft + activation.width, cellPixels) - Math.ceil(activation.width / cellPixels) : nearestCellEdge(frameLeft, cellPixels);
  const bottom = nearestCellEdge((drawnFrameY(activation.y / SIXTEENTHS, activation.offset?.y ?? 0, tileSize) + 1) * tileSize, cellPixels);
  const feetLeft = Math.floor((self.x - FOOT_HALF_WIDTH) / AUTHORING_CELL);
  const feetRight = Math.floor((self.x + FOOT_HALF_WIDTH - 1) / AUTHORING_CELL);
  const feetTop = Math.floor((self.y - FOOT_HEIGHT) / AUTHORING_CELL);
  const feetBottom = Math.floor((self.y - 1) / AUTHORING_CELL);
  for (const [column, rowsUp] of activation.cells) {
    const x = left + column;
    const y = bottom - rowsUp - 1;
    if (feetRight >= x && feetLeft <= x && feetBottom >= y && feetTop <= y) {
      return true;
    }
  }
  return false;
}
